import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api.js';
import { useScopeKey } from './useScopeKey.js';

// ── Query keys ────────────────────────────────────────────────────────────────

function movementsKey(scopeKey, filters) {
  return ['petty-cash', 'movements', scopeKey, filters];
}

function summaryKey(scopeKey, from, to) {
  return ['petty-cash', 'summary', scopeKey, from, to];
}

// ── Queries ───────────────────────────────────────────────────────────────────

/**
 * Liste paginée des mouvements de caisse.
 * filters : { from, to, category_id, kind, search, limit, offset }
 */
export function usePettyCashMovements(filters = {}) {
  const scopeKey = useScopeKey();

  const qs = new URLSearchParams();
  if (filters.from) qs.set('from', filters.from);
  if (filters.to) qs.set('to', filters.to);
  if (filters.category_id) qs.set('category_id', filters.category_id);
  if (filters.kind) qs.set('kind', filters.kind);
  if (filters.search) qs.set('search', filters.search);
  if (filters.limit != null) qs.set('limit', String(filters.limit));
  if (filters.offset != null) qs.set('offset', String(filters.offset));
  const qsStr = qs.toString();

  return useQuery({
    queryKey: movementsKey(scopeKey, filters),
    queryFn: () => api.get(`/petty-cash/movements${qsStr ? `?${qsStr}` : ''}`),
    staleTime: 30_000,
    enabled: scopeKey !== 'loading',
  });
}

/**
 * KPIs + série temporelle de solde pour la période donnée.
 * from, to : 'YYYY-MM-DD'
 */
export function usePettyCashSummary(from, to) {
  const scopeKey = useScopeKey();

  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  const qsStr = qs.toString();

  return useQuery({
    queryKey: summaryKey(scopeKey, from, to),
    queryFn: () => api.get(`/petty-cash/summary${qsStr ? `?${qsStr}` : ''}`),
    staleTime: 30_000,
    enabled: scopeKey !== 'loading',
  });
}

// ── Shared invalidation ───────────────────────────────────────────────────────

function usePettyCashMutation(mutateFn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: mutateFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['petty-cash'] });
    },
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

/**
 * Crée un mouvement.
 * body : { kind, amount_cents, movement_date, category_id?, comment?, shopify_order_id? }
 */
export function useCreatePettyCashMovement() {
  return usePettyCashMutation((body) => api.post('/petty-cash/movements', body));
}

/**
 * Met à jour un mouvement (kind immuable).
 * { id, ...body partiel }
 */
export function useUpdatePettyCashMovement() {
  return usePettyCashMutation(({ id, ...body }) =>
    api.patch(`/petty-cash/movements/${id}`, body)
  );
}

/**
 * Supprime un mouvement.
 */
export function useDeletePettyCashMovement() {
  return usePettyCashMutation((id) => api.delete(`/petty-cash/movements/${id}`));
}
